import React, { Fragment, useEffect } from 'react';
import { HashRouter as Router, Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LocaleProvider } from '../utils/LocaleContext';
import RequireRole from '../components/RequireRole';
import RequireFeature from '../components/RequireFeature';

import LoginScreen from '../screens/LoginScreen';
import ForgotPassword from '../screens/ForgotPassword';

// Legacy /clients route managed organizations; preserved at /orgs-admin so the
// new Clients module (insured persons / customers) can take over /clients.
import OrgsAdminScreen from '../screens/ClientsScreen';
import ClientsListScreen from '../screens/ClientsListScreen';
import ClientDetail from '../screens/ClientsListScreen/ClientDetail';
import UsersScreen from '../screens/UsersScreen';

import FileExchangeScreen from '../screens/FileExchangeScreen';
import DownloadFileScreen from '../screens/FileExchangeScreen/DownloadFileScreen';
import MyProfileScreenLegacy from '../screens/MyProfileScreen';
import LogsScreen from '../screens/Logs';
import ChatScreen from '../screens/ChatScreenNew';
import ChatRequestsScreen from '../screens/ChatScreenNew/ChatRequests';

import VideoCallScreen from '../screens/VideoCallScreen';

import VideoState from '../screens/VideoCallScreen/context/VideoState';
import VideoTranscript from '../screens/VideoTranscript';
import ViewFile from '../screens/FileExchangeScreen/ViewFile';
import EmailClient from '../screens/EmailClient';
import SecureEmail from '../screens/SecureEmail';
import { io } from 'socket.io-client';
import ErrorBoundary from '../screens/ErrorBoundary/ErrorBoundary';
import WebsiteFormsScreen from '../screens/WebsiteForms';
import GroupVideoCallScreen from '../screens/GroupVideoCallScreen';
import VideoMeetingsScreen from '../screens/VideoMeetingsScreen';
import GuestVideoScreen from '../screens/GuestVideoScreen';
import SignPDFScreen from '../screens/SignPDF';
import ESignaturesScreen from '../screens/ESignaturesScreen';
import ESignatureDetailScreen from '../screens/ESignaturesScreen/DetailScreen';
import SigningScreen from '../screens/SigningScreen';

// ─── New Regure Pages ─────────────────────────────────────
import AuditIntegrityScreen from '../screens/AuditIntegrityScreen';
import IntegrationsScreen from '../screens/IntegrationsScreen';
import VerifyAuditScreen from '../screens/VerifyAuditScreen';
import DashboardScreen from '../screens/DashboardScreen';
import ClaimsScreen from '../screens/ClaimsScreen';
import ClaimDetail from '../screens/ClaimsScreen/ClaimDetail';
import ClaimNew from '../screens/ClaimsScreen/ClaimNew';
import PoliciesScreen from '../screens/PoliciesScreen';
import OffersScreen from '../screens/OffersScreen';
import BookImportScreen from '../screens/BookImportScreen';
import InvoicingScreen from '../screens/InvoicingScreen';
import CommissionsScreen from '../screens/CommissionsScreen';
import BrokersScreen from '../screens/BrokersScreen';
import RiskCarriersScreen from '../screens/RiskCarriersScreen';
import ComplianceReportsScreen from '../screens/ComplianceReportsScreen';
import DataResidencyScreen from '../screens/DataResidencyScreen';
import DSARScreen from '../screens/DSARScreen';
import APIKeysScreen from '../screens/APIKeysScreen';
import WebhooksScreen from '../screens/WebhooksScreen';
import WorkflowRulesScreen from '../screens/WorkflowRulesScreen';
import ClaimsAnalyticsScreen from '../screens/ClaimsAnalyticsScreen';
import DemoScreen from '../screens/DemoScreen';
import UnmatchedDocumentsScreen from '../screens/UnmatchedDocumentsScreen';
import AccountSettingsScreen from '../screens/AccountSettingsScreen';
import AIGovernanceScreen from '../screens/AIGovernanceScreen';
import RiskSignalsScreen from '../screens/RiskSignalsScreen';
import BordereauxScreen from '../screens/BordereauxScreen';
import ReinsuranceSubmissionIntake from '../screens/ReinsuranceScreen/SubmissionIntelligence';
import ReinsuranceSubmissionsList from '../screens/ReinsuranceScreen/SubmissionsList';
import ReinsurancePlacementList from '../screens/ReinsuranceScreen/PlacementList';
import ReinsuranceClosingsList from '../screens/ReinsuranceScreen/ClosingsList';
import ReinsuranceClaimsValidation from '../screens/ReinsuranceScreen/ClaimsValidation';
import ReinsurancePortfolio from '../screens/ReinsuranceScreen/PortfolioScreen';
import ReinsuranceDeals from '../screens/ReinsuranceScreen/DealsList';
import ReinsuranceDealDetail from '../screens/ReinsuranceScreen/DealDetail';
import ReinsuranceFXRates from '../screens/ReinsuranceScreen/FXRates';
import ReinsuranceClaimDetail from '../screens/ReinsuranceScreen/ClaimDetail';
import ReinsuranceEndorsementDetail from '../screens/ReinsuranceScreen/EndorsementDetail';
import ReinsurancePlacementQueue from '../screens/ReinsuranceScreen/PlacementQueue';
import ReinsurancePlacementBoard from '../screens/ReinsuranceScreen/PlacementBoard';
import ReinsuranceSanctionsChecks from '../screens/ReinsuranceScreen/SanctionsChecks';
import RedirectToList from '../screens/ReinsuranceScreen/RedirectToList';
// Second wave (2026-09). Each screen sits behind its own RequireFeature, so a
// typed URL is refused for a tenant that does not have the module — the
// sidebar hiding the item is not, on its own, access control.
import ReinsuranceDashboard from '../screens/ReinsuranceScreen/Dashboard';
import ReinsuranceTreatiesList from '../screens/ReinsuranceScreen/TreatiesList';
import ReinsuranceAccountsList from '../screens/ReinsuranceScreen/AccountsList';
import ReinsuranceRecoveriesList from '../screens/ReinsuranceScreen/RecoveriesList';
import ReinsuranceCounterpartiesList from '../screens/ReinsuranceScreen/CounterpartiesList';
import ReinsuranceComplianceChecks from '../screens/ReinsuranceScreen/ComplianceChecks';
import ReinsuranceRenewalsList from '../screens/ReinsuranceScreen/RenewalsList';
import PrivacyPolicyScreen from '../screens/PrivacyPolicyScreen';
import TermsOfServiceScreen from '../screens/TermsOfServiceScreen';
import SuperAdminDashboard from '../screens/SuperAdminDashboard';
import PlatformOverview from '../screens/PlatformOverview';
import PlatformTenants from '../screens/PlatformTenants';
import TenantDetail from '../screens/PlatformTenants/TenantDetail';
import OnboardOrgScreen from '../screens/OnboardOrgScreen';
import OrgDetailScreen from '../screens/OrgDetailScreen';
import PolicyDetailScreen from '../screens/PolicyDetailScreen';

// ─── Portal Screens (Policyholder & Broker) ───────────────
import MyPoliciesScreen from '../screens/PortalScreens/MyPoliciesScreen';
import MyClaimsScreen from '../screens/PortalScreens/MyClaimsScreen';
import MyClientsScreen from '../screens/PortalScreens/MyClientsScreen';
import MyCommissionsScreen from '../screens/PortalScreens/MyCommissionsScreen';
import MyClaimDetailScreen from '../screens/PortalScreens/MyClaimDetailScreen';
import MyDocumentsScreen from '../screens/PortalScreens/MyDocumentsScreen';
import MyProfileScreen from '../screens/PortalScreens/MyProfileScreen';
import MyPolicyDetailScreen from '../screens/PortalScreens/MyPolicyDetailScreen';

import NotFoundGate from '../screens/NotFoundScreen/NotFoundGate';

const ENABLE_COMMS = process.env.REACT_APP_ENABLE_CHAT_VIDEO_ESIGN === 'true';
const ENABLE_EXPERIMENTAL = process.env.REACT_APP_ENABLE_EXPERIMENTAL_MODULES === 'true';

const Navigation = ({ socket, setSocket }) => {
  const loggedinUser = useSelector(state => state.loggedinUser);
  const userLogin = useSelector(state => state.userLogin);
  const orgsList = useSelector(state => state.orgsList);
  const tenantConfig = useSelector(state => state.tenantConfig?.config);
  const { i18n } = useTranslation();

  // Resolve language priority:
  //   1. user.preferences.language      — personal override (Locale & Display)
  //   2. tenantConfig.locale.language   — SOURCE OF TRUTH for tenant default
  //                                       (managed via Tenant Management > Locale)
  //   3. user.orgPreferredLanguage      — legacy Org.preferredLanguage (deprecated)
  //   4. orgsList entry's preferredLanguage — same legacy path
  //   5. localStorage                    — persisted per-browser
  //   6. 'en'                            — final fallback
  useEffect(() => {
    // Try loggedinUser first (populated by getLoggedinUserAction), fall back to userLogin
    const user = loggedinUser?.userInfo?.data?.data || userLogin?.userInfo?.data?.user;
    const orgs = Array.isArray(orgsList?.orgs) ? orgsList.orgs : [];

    // Pre-auth pages (login, forgot-password, legal) must not adopt the
    // previous session's tenant / saved language. i18n.js already picked
    // navigator.language | 'en' at boot; leave it alone until we actually
    // have an authenticated user.
    if (!user) return;

    // Tenant-config language is authoritative once loaded. This lets admins
    // fix a locale globally for a tenant via Tenant Management > Locale
    // without touching per-user prefs. Skips the '(unconfigured tenant)'
    // placeholder that the middleware returns when no TenantConfig exists.
    const tenantLanguage = tenantConfig?._isDefault ? null : tenantConfig?.locale?.language;

    // Legacy Org.preferredLanguage fallback (kept until every tenant has
    // a TenantConfig doc)
    let orgLanguage = user?.orgPreferredLanguage;
    if (!orgLanguage && user?.org && orgs.length > 0) {
      const userOrg = orgs.find(org => org._id === user.org);
      orgLanguage = userOrg?.preferredLanguage;
    }

    const userLanguage = user?.preferences?.language;
    const savedLanguage = localStorage.getItem('regure_locale_settings') && JSON.parse(localStorage.getItem('regure_locale_settings')).language;

    const resolvedLanguage = userLanguage || tenantLanguage || orgLanguage || savedLanguage || 'en';

    // console.log(
    //   `[LANGUAGE-RES] User: ${user?.name} | UserPref: ${userLanguage} | OrgPrefFromUser: ${user?.orgPreferredLanguage} | OrgPrefFromList: ${orgLanguage} | Saved: ${savedLanguage} | Resolved: ${resolvedLanguage}`,
    // );

    // Apply language to i18n if different from current
    if (i18n.language !== resolvedLanguage) {
      console.log(`[LANGUAGE-RES] Changing i18n from ${i18n.language} to ${resolvedLanguage}`);
      i18n.changeLanguage(resolvedLanguage);
    }
  }, [userLogin, orgsList, tenantConfig, i18n, loggedinUser]);

  return (
    <LocaleProvider>
      <Router>
        <Route exact path='/login' component={LoginScreen} />
        <Route exact path='/forgot-password' component={ForgotPassword} />
        <Route exact path='/privacy' component={PrivacyPolicyScreen} />
        <Route exact path='/terms' component={TermsOfServiceScreen} />
        {/* Public pages — no auth required */}
        <Route path='/guest-video/:roomId' component={GuestVideoScreen} />
        <Route exact path='/verify-audit' component={VerifyAuditScreen} />

        <VideoState socket={socket}>
          {/* ── New Regure Pages ──────────────────────────── */}
          <Route exact path='/dashboard'>
            <RequireRole resource='claims' action='read'>
              <DashboardScreen socket={socket} />
            </RequireRole>
          </Route>
          <Route exact path='/claims/new'>
            <RequireFeature featureKey='claims'>
              <RequireRole resource='claims' action='create'><ClaimNew /></RequireRole>
            </RequireFeature>
          </Route>
          {/* :id constrained to 24-char hex (Mongo ObjectId). Without the
              constraint /:id also matches literal path segments like "new"
              and both routes render simultaneously — the ClaimNew form
              stacks on top of the "Claim not found" ClaimDetail. This is
              the same double-mount class as T-10.4 on ClaimDetail. */}
          <Route exact path='/claims/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='claims'>
              <RequireRole resource='claims' action='read'><ClaimDetail /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/policies'>
            <RequireRole resource='policies' action='read'><PoliciesScreen /></RequireRole>
          </Route>
          {/* :id constrained to ObjectId — see /claims/:id comment. */}
          <Route exact path='/policies/:id([a-f0-9]{24})'>
            <RequireRole resource='policies' action='read'><PolicyDetailScreen /></RequireRole>
          </Route>
          <Route exact path='/offers'>
            <RequireFeature featureKey='offers'>
              <RequireRole resource='offers' action='read'><OffersScreen /></RequireRole>
            </RequireFeature>
          </Route>
          {/* Book Import is a bulk CREATE, so it is gated on offers.create
              rather than offers.read — a broker who may see the book must not
              be able to write one. Declared before /offers/:id would be, for
              the same reason the API declares /offers/import above it. */}
          <Route exact path='/offers/import'>
            <RequireFeature featureKey='offers'>
              <RequireRole resource='offers' action='create'><BookImportScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/invoicing'>
            <RequireFeature featureKey='invoicing'>
              <RequireRole resource='invoices' action='read'><InvoicingScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/brokers'>
            <RequireFeature featureKey='brokers'>
              <RequireRole resource='brokers' action='read'><BrokersScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/risk-carriers'>
            <RequireFeature featureKey='riskCarriers'>
              <RequireRole resource='riskCarriers' action='read'><RiskCarriersScreen /></RequireRole>
            </RequireFeature>
          </Route>
          {/* Compliance / residency / DSAR — gated by RBAC only. The
              ENABLE_EXPERIMENTAL flag used to redirect these to /dashboard,
              which then bounced SaaS God to /platform (A6 test failure).
              Nav-item visibility for non-admin roles is still controlled
              by the tenant feature-flag config. */}
          <Route exact path='/compliance-reports'>
            <RequireFeature featureKey='compliance'>
              <RequireRole resource='compliance' action='read'><ComplianceReportsScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/data-residency'>
            <RequireFeature featureKey='dataResidency'>
              <RequireRole resource='dataResidency' action='read'><DataResidencyScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/dsar-requests'>
            <RequireFeature featureKey='dsar'>
              <RequireRole resource='dsar' action='read'><DSARScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/api-keys'>
            <RequireFeature featureKey='apiKeys'>
              <RequireRole resource='apiKeys' action='read'><APIKeysScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/webhooks'>
            <RequireFeature featureKey='webhooks'>
              <RequireRole resource='webhooks' action='read'><WebhooksScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/workflow-rules'>
            <RequireFeature featureKey='workflowAutomation'>
              <RequireRole resource='workflows' action='read'><WorkflowRulesScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/claims-analytics'>
            <RequireFeature featureKey='claimsAnalytics'>
              <RequireRole resource='claims' action='read'><ClaimsAnalyticsScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/demo' component={DemoScreen} />
          <Route exact path='/unmatched-documents'>
            <RequireRole resource='documents' action='read'><UnmatchedDocumentsScreen /></RequireRole>
          </Route>
          <Route exact path='/account-settings'>
            <RequireRole resource='orgs' action='read'><AccountSettingsScreen /></RequireRole>
          </Route>
          {/* Team Management — canonical route is /users, alias here for
              typed-URL access. Both need to be guarded so non-privileged
              roles can't bypass with a manual URL. */}
          <Route exact path='/team-management'>
            <RequireRole resource='users' action='read'><UsersScreen /></RequireRole>
          </Route>
          {/* New-record routes for direct URL access. The "new" sub-pages
              don't exist as standalone screens (creation is via modal on
              the list) — redirect to the list, but only after the role
              check so the user hits the permission toast instead of a raw
              404. */}
          <Route exact path='/clients/new'>
            <RequireRole resource='clients' action='create'><Redirect to='/clients' /></RequireRole>
          </Route>
          <Route exact path='/offers/new'>
            <RequireRole resource='offers' action='create'><Redirect to='/offers' /></RequireRole>
          </Route>
          <Route exact path='/policies/new'>
            <RequireRole resource='policies' action='create'><Redirect to='/policies' /></RequireRole>
          </Route>
          {/* Risk Signals always accessible (read-only view) */}
          <Route exact path='/risk-signals'>
            <RequireFeature featureKey='riskSignals'>
              <RequireRole resource='riskSignals' action='read'><RiskSignalsScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/ai-governance'>
            <RequireFeature featureKey='aiGovernance'>
              <RequireRole resource='aiGovernance' action='read'><AIGovernanceScreen /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/bordereaux'>
            <RequireFeature featureKey='bordereaux'>
              <RequireRole resource='bordereaux' action='read'><BordereauxScreen /></RequireRole>
            </RequireFeature>
          </Route>
          {/* Reinsurance module — one route per sub-flag. Each RequireFeature
              gate matches a TenantConfig key, and the backend router
              enforces the same gate; a feature flag answers "is this sold
              to this tenant", RequireRole answers "may this user do it",
              and neither substitutes for the other.

              Submissions, Placement and Closings are three views of one
              ReinsuranceDeal at different points in its life — see
              backend/controllers/reinsuranceDealStageController.js. The
              older per-deal workspaces (deal detail, placement board,
              claim validation) keep their routes: they carry actions the
              list drawers do not, so they are workspaces rather than
              second copies of the same read view. */}
          <Route exact path='/reinsurance/submissions/intake'>
            <RequireFeature featureKey='reinsuranceSubmissions'>
              <RequireRole resource='reinsurance' action='create'><ReinsuranceSubmissionIntake /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/submissions'>
            <RequireFeature featureKey='reinsuranceSubmissions'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceSubmissionsList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/deals'>
            <RequireFeature featureKey='reinsuranceSubmissions'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceDeals /></RequireRole>
            </RequireFeature>
          </Route>
          {/* :id constrained to ObjectId to prevent /reinsurance/deals/foo
              from crashing the detail fetch. Mirrors the /claims/:id and
              /policies/:id pattern used elsewhere in this file. */}
          <Route exact path='/reinsurance/deals/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceSubmissions'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceDealDetail /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/fx-rates'>
            <RequireFeature featureKey='reinsuranceClosings'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceFXRates /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/placement/queue'>
            <RequireFeature featureKey='reinsurancePlacement'>
              <RequireRole resource='reinsurance' action='read'><ReinsurancePlacementQueue /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/placement'>
            <RequireFeature featureKey='reinsurancePlacement'>
              <RequireRole resource='reinsurance' action='read'><ReinsurancePlacementList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/deals/:id([a-f0-9]{24})/placement'>
            <RequireFeature featureKey='reinsurancePlacement'>
              <RequireRole resource='reinsurance' action='read'><ReinsurancePlacementBoard /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/sanctions'>
            <RequireFeature featureKey='reinsuranceSanctions'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceSanctionsChecks /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/closings'>
            <RequireFeature featureKey='reinsuranceClosings'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceClosingsList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/claims'>
            <RequireFeature featureKey='reinsuranceClaimsValidation'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceClaimsValidation /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/claims/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceClaimsValidation'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceClaimDetail /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/endorsements/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceEndorsements'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceEndorsementDetail /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/portfolio'>
            <RequireFeature featureKey='reinsurancePortfolio'>
              <RequireRole resource='reinsurance' action='read'><ReinsurancePortfolio /></RequireRole>
            </RequireFeature>
          </Route>

          {/* ── Reinsurance second wave (2026-09) ──────────────────
              The module landing page. Gated on reinsurancePortfolio, the
              flag a tenant already has when it has the reinsurance
              overview at all — the panels a tenant has no modules for
              come back empty rather than being hidden. */}
          <Route exact path='/reinsurance'>
            <RequireFeature featureKey='reinsuranceDashboard'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceDashboard /></RequireRole>
            </RequireFeature>
          </Route>

          {/* Module 6 — Treaties & Facultative */}
          <Route exact path='/reinsurance/treaties'>
            <RequireFeature featureKey='reinsuranceTreaties'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceTreatiesList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/treaties/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceTreaties'>
              <RequireRole resource='reinsurance' action='read'><RedirectToList to='/reinsurance/treaties' /></RequireRole>
            </RequireFeature>
          </Route>

          {/* Module 7 — Bordereaux & Accounting */}
          <Route exact path='/reinsurance/accounting'>
            <RequireFeature featureKey='reinsuranceAccounting'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceAccountsList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/accounting/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceAccounting'>
              <RequireRole resource='reinsurance' action='read'><RedirectToList to='/reinsurance/accounting' /></RequireRole>
            </RequireFeature>
          </Route>

          {/* Module 8 — Recoveries */}
          <Route exact path='/reinsurance/recoveries'>
            <RequireFeature featureKey='reinsuranceRecoveries'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceRecoveriesList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/recoveries/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceRecoveries'>
              <RequireRole resource='reinsurance' action='read'><RedirectToList to='/reinsurance/recoveries' /></RequireRole>
            </RequireFeature>
          </Route>

          {/* Module 9 — Counterparties & Compliance. Read is `validate`, not
              `read`: the registry carries KYC and sanctions state. */}
          <Route exact path='/reinsurance/counterparties'>
            <RequireFeature featureKey='reinsuranceCounterparties'>
              <RequireRole resource='reinsurance' action='validate'><ReinsuranceCounterpartiesList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/counterparties/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceCounterparties'>
              <RequireRole resource='reinsurance' action='validate'><RedirectToList to='/reinsurance/counterparties' /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/compliance'>
            <RequireFeature featureKey='reinsuranceCounterparties'>
              <RequireRole resource='reinsurance' action='validate'><ReinsuranceComplianceChecks /></RequireRole>
            </RequireFeature>
          </Route>

          {/* Module 10 — Renewals */}
          <Route exact path='/reinsurance/renewals'>
            <RequireFeature featureKey='reinsuranceRenewals'>
              <RequireRole resource='reinsurance' action='read'><ReinsuranceRenewalsList /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/reinsurance/renewals/:id([a-f0-9]{24})'>
            <RequireFeature featureKey='reinsuranceRenewals'>
              <RequireRole resource='reinsurance' action='read'><RedirectToList to='/reinsurance/renewals' /></RequireRole>
            </RequireFeature>
          </Route>
          <Route exact path='/platform'>
            <RequireRole resource='platform' action='read'><PlatformOverview /></RequireRole>
          </Route>
          <Route exact path='/platform/tenants'>
            <RequireRole resource='platform' action='read'><PlatformTenants /></RequireRole>
          </Route>
          <Route exact path='/platform/tenants/:orgId'>
            <RequireRole resource='platform' action='read'><TenantDetail /></RequireRole>
          </Route>
          <Route exact path='/orgs/onboard'>
            <RequireRole resource='platform' action='write'><OnboardOrgScreen /></RequireRole>
          </Route>
          <Route exact path='/orgs/:id'>
            <RequireRole resource='orgs' action='read'><OrgDetailScreen /></RequireRole>
          </Route>

          {/* ── Portal Pages (Policyholder & Broker) ────────── */}
          <Route exact path='/portal/my-policies'>
            <RequireFeature featureKey='policies'><MyPoliciesScreen /></RequireFeature>
          </Route>
          <Route exact path='/portal/my-policies/:id'>
            <RequireFeature featureKey='policies'><MyPolicyDetailScreen /></RequireFeature>
          </Route>
          <Route exact path='/portal/my-claims'>
            <RequireFeature featureKey='claims'><MyClaimsScreen /></RequireFeature>
          </Route>
          <Route exact path='/portal/my-claims/:id'>
            <RequireFeature featureKey='claims'><MyClaimDetailScreen /></RequireFeature>
          </Route>
          <Route exact path='/portal/my-clients'>
            <RequireFeature featureKey='clients'><MyClientsScreen /></RequireFeature>
          </Route>
          <Route exact path='/portal/my-commissions'>
            <RequireFeature featureKey='commissions'><MyCommissionsScreen /></RequireFeature>
          </Route>
          <Route exact path='/portal/my-documents'>
            <RequireFeature featureKey='documentProcessing'><MyDocumentsScreen /></RequireFeature>
          </Route>
          <Route exact path='/portal/my-profile' component={MyProfileScreen} />

          {/* ── Portal Redirects ──────────────────────────── */}
          {/* Redirect portal users away from back-office pages */}
          <Route exact path='/claims'>
            {userLogin?.userInfo?.data?.data?.role === 'policyholder' ? (
              <Redirect to='/portal/my-claims' />
            ) : (
              <RequireFeature featureKey='claims'>
                <RequireRole resource='claims' action='read'><ClaimsScreen /></RequireRole>
              </RequireFeature>
            )}
          </Route>

          <Route exact path='/commissions'>
            {userLogin?.userInfo?.data?.data?.role === 'broker' ? (
              <Redirect to='/portal/my-commissions' />
            ) : userLogin?.userInfo?.data?.data?.role === 'policyholder' ? (
              <Redirect to='/portal/my-policies' />
            ) : (
              <RequireFeature featureKey='commissions'>
                <RequireRole resource='commissions' action='read'><CommissionsScreen /></RequireRole>
              </RequireFeature>
            )}
          </Route>

          {/* ── Existing Pages ────────────────────────────── */}
          <Route exact path='/' component={FileExchangeScreen} />

          <Route exact path='/secure-files' component={FileExchangeScreen} />
          <Route exact path='/sign-document' component={SignPDFScreen} />
          <Route exact path='/files/:id' component={ViewFile} />
          <Route exact path='/share-file' component={DownloadFileScreen} />

          <Route exact path='/clients'>
            <RequireRole resource='clients' action='read'><ClientsListScreen /></RequireRole>
          </Route>
          {/* :id constrained to ObjectId — see /claims/:id comment. */}
          <Route exact path='/clients/:id([a-f0-9]{24})'>
            <RequireRole resource='clients' action='read'><ClientDetail /></RequireRole>
          </Route>
          <Route exact path='/orgs-admin'>
            <RequireRole resource='platform' action='read'><OrgsAdminScreen /></RequireRole>
          </Route>
          <Route exact path='/users'>
            <RequireRole resource='users' action='read'><UsersScreen /></RequireRole>
          </Route>
          <Route exact path='/audit-logs'>
            <RequireRole resource='audit' action='read'><LogsScreen /></RequireRole>
          </Route>
          <Route exact path='/audit-integrity'>
            <RequireRole resource='integrity' action='read'><AuditIntegrityScreen /></RequireRole>
          </Route>
          <Route exact path='/integrations'>
            <RequireRole resource='orgs' action='update'><IntegrationsScreen /></RequireRole>
          </Route>
          <Route exact path='/Error' component={ErrorBoundary} />

          {/* {userLogin &&
          userLogin.userInfo &&
          userLogin?.userInfo?.data &&
          userLogin?.userInfo?.data.user &&
          userLogin?.userInfo?.data.user.modules &&
          userLogin?.userInfo?.data.user.modules.websiteForms && <Route exact path='/website-forms' component={WebsiteFormsScreen} />} */}
          <Route exact path='/website-forms' component={WebsiteFormsScreen} />

          <Fragment>
            <Route exact path='/chat'>
              <ChatScreen socket={socket} />
            </Route>
            <Route exact path='/chat-requests'>
              <ChatRequestsScreen socket={socket} />
            </Route>
            {!ENABLE_COMMS && (
              <Fragment>
                <Route exact path='/make/:call/:type/:userId/:name/:socketId'>
                  <VideoCallScreen socket={socket} />
                </Route>
                <Route exact path='/group-video-call'>
                  <VideoMeetingsScreen socket={socket} />
                </Route>
                <Route exact path='/group-video-call/:roomId'>
                  <GroupVideoCallScreen socket={socket} />
                </Route>
                <Route exact path='/video-transcript' component={VideoTranscript} />
                <Route exact path='/e-signatures' component={ESignaturesScreen} />
                <Route exact path='/e-signatures/:id' component={ESignatureDetailScreen} />
              </Fragment>
            )}
            {ENABLE_COMMS && (
              <Fragment>
                {/* <Route exact path='/chat'><Redirect to='/dashboard' /></Route> */}
                <Route exact path='/group-video-call'>
                  <Redirect to='/dashboard' />
                </Route>
                <Route exact path='/e-signatures'>
                  <Redirect to='/dashboard' />
                </Route>
              </Fragment>
            )}
            {/* Public signing page — no auth required, uses token */}
            <Route exact path='/sign/:requestId' component={SigningScreen} />
            <Route exact path='/email'>
              {/* <EmailClient socket={socket} /> */}
              <SecureEmail socket={socket} />
            </Route>
          </Fragment>

          <Route exact path='/my-profile' component={MyProfileScreenLegacy} />
        </VideoState>
        {/* 404 fallback (D-03) — rendered outside VideoState so it's
            not gated by auth/socket setup. See NotFoundGate for the
            known-path list. */}
        <NotFoundGate />
      </Router>
    </LocaleProvider>
  );
};

export default Navigation;
